import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Menu } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { swapLangPath, booksPath, servicesPath } from "../lib/paths";

const NAV = {
  de: [
    { label: "Bücher", to: booksPath("de") },
    { label: "Verlag", href: "#verlag" },
    { label: "Werkstatt", to: servicesPath("de") },
    { label: "Kontakt", href: "#kontakt" },
  ],
  es: [
    { label: "Libros", to: booksPath("es") },
    { label: "Editorial", href: "#verlag" },
    { label: "Taller", to: servicesPath("es") },
    { label: "Contacto", href: "#kontakt" },
  ],
};

export default function Header({ currentLanguage = "de" }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const next = currentLanguage === "de" ? "es" : "de";
    navigate(swapLangPath(location.pathname, currentLanguage, next) + location.search);
  };

  const home = `/${currentLanguage}`;
  const items = NAV[currentLanguage].map((i) =>
    i.href ? { ...i, href: `${home}${i.href}` } : i
  );

  const enlace =
    "rotulo text-hueso/75 hover:text-hueso transition-colors duration-200";

  return (
    <header className="sticky top-0 z-50 bg-tinta/95 backdrop-blur-sm border-b border-borde">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 h-20 flex items-center justify-between gap-4">
        <Link
          to={home}
          className="flex items-center gap-3 shrink-0 group"
          aria-label="Blauer Wolf Verlag"
        >
          <img
            src="/sello-bwv.png"
            alt=""
            width="40"
            height="40"
            className="w-9 h-9 sm:w-10 sm:h-10 opacity-90 group-hover:opacity-100 transition-opacity"
          />
          <span className="font-vers text-hueso whitespace-nowrap leading-none text-[15px] sm:text-lg tracking-[0.12em]">
            Blauer Wolf Verlag
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {items.map((item) =>
            item.to ? (
              <Link key={item.to} to={item.to} className={enlace}>
                {item.label}
              </Link>
            ) : (
              <Link key={item.href} to={item.href} className={enlace}>
                {item.label}
              </Link>
            )
          )}
          <button
            onClick={toggleLanguage}
            className="rotulo text-lobo hover:text-loboClaro border border-borde hover:border-lobo px-3 py-1.5 transition-colors"
          >
            {currentLanguage === "de" ? "Español" : "Deutsch"}
          </button>
        </nav>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden p-2 -mr-2 text-hueso/80 hover:text-hueso"
          aria-label={menuOpen ? "Menü schließen" : "Menü"}
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="lg:hidden border-t border-borde bg-tinta"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="px-5 sm:px-8 py-5 flex flex-col gap-4">
              {items.map((item) =>
                item.to ? (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={enlace}
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={enlace}
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                )
              )}
              <button
                onClick={() => {
                  toggleLanguage();
                  setMenuOpen(false);
                }}
                className="rotulo text-lobo text-left pt-2 border-t border-borde"
              >
                {currentLanguage === "de" ? "Español" : "Deutsch"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
